import React, { useState, useEffect, useRef, useMemo } from 'react';
import { AlertTriangle, Check, CheckSquare, Globe, Loader2, Sparkles, User } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import type { SelectedDocsTabProps } from './types';
import DocTooltip, { useDocTooltip } from '../../../components/sidebar/DocTooltip';
import { formatPeriod } from '../../../utils/formatPeriod';

const AUTO_DISMISS_MS = 10_000;

/**
 * "Seleccionados" tab — unified view of ALL currently selected documents.
 * Shows scope badges (🌐 shared, 👤 private) consistent with Recientes,
 * plus AI visual cues (left accent border, reason on hover) for AI-selected docs.
 */
const SelectedDocsTab: React.FC<SelectedDocsTabProps> = ({
  selectedDocuments,
  sources,
  suggestions,
  onDeselect,
  documentSelectionMode,
  userPromptNeeded,
  isSuggestionsLoading,
}) => {
  const { theme } = useTheme();
  const { tooltip, onMouseEnter: onDocMouseEnter, onMouseLeave: onDocMouseLeave } = useDocTooltip();

  // Build reason lookup map from suggestions for O(1) access
  const reasonMap = useMemo(() => {
    const map = new Map<string, { reason?: string; strategy?: string }>();
    suggestions.forEach((s) => {
      if (s.reason || s.strategy) map.set(s.id, { reason: s.reason, strategy: s.strategy });
    });
    return map;
  }, [suggestions]);

  // Self-contained low confidence banner state
  const [lowConfidenceDismissed, setLowConfidenceDismissed] = useState(false);
  const hasAiDocs = selectedDocuments.some((d) => sources.get(d.id) === 'ai-system');
  const showLowConfidence = !lowConfidenceDismissed && userPromptNeeded && hasAiDocs;

  // Auto-dismiss timer
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (showLowConfidence) {
      timerRef.current = setTimeout(() => setLowConfidenceDismissed(true), AUTO_DISMISS_MS);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [showLowConfidence]);

  // Reset dismiss state when new suggestions arrive
  const prevUserPromptRef = useRef(userPromptNeeded);
  useEffect(() => {
    if (userPromptNeeded && !prevUserPromptRef.current) {
      setLowConfidenceDismissed(false);
    }
    prevUserPromptRef.current = userPromptNeeded;
  }, [userPromptNeeded]);

  // Empty state
  if (selectedDocuments.length === 0 && !isSuggestionsLoading) {
    return (
      <div className={`px-4 py-8 text-center ${theme.colors.text.secondary}`}>
        <CheckSquare className="w-6 h-6 mx-auto mb-2 opacity-50" />
        <div className="text-xs">Sin documentos seleccionados</div>
        <div className={`text-xs ${theme.colors.text.muted} mt-1 opacity-70`}>
          {documentSelectionMode === 'smart'
            ? 'Las sugerencias aparecerán al escribir'
            : 'Selecciona documentos de Pública o Personal'}
        </div>
      </div>
    );
  }

  // Loading state (only when no docs yet)
  if (isSuggestionsLoading && selectedDocuments.length === 0) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-center gap-2 text-nb-accent/60">
          <Sparkles className="w-4 h-4 animate-pulse" />
          <span className={`text-xs ${theme.colors.text.secondary}`}>
            Buscando documentos relevantes...
          </span>
        </div>
        {[1, 2, 3].map((i) => (
          <div key={`skeleton-${i}`} className="flex items-center gap-2 px-3 py-1.5 animate-pulse">
            <div className="w-4 h-4 rounded border-2 border-nb-accent/20 flex-shrink-0" />
            <div className="flex-1 space-y-1">
              <div className="h-3 rounded bg-nb-accent/10" style={{ width: `${60 + i * 12}%` }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Low confidence banner */}
      {showLowConfidence && (
        <div
          className="flex items-center gap-2 mx-3 mt-2 mb-1 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200 rounded-lg px-3 py-2 text-xs"
          aria-live="polite"
        >
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="flex-1">Smart no está seguro de estos documentos. Revísalos.</span>
          <button
            type="button"
            onClick={() => setLowConfidenceDismissed(true)}
            className="px-2 py-0.5 rounded text-[10px] font-medium bg-amber-200 hover:bg-amber-300 dark:bg-amber-700 dark:hover:bg-amber-600 dark:text-amber-100 transition-colors"
          >
            Usar estos
          </button>
          <button
            type="button"
            onClick={() => {
              // Deselect all AI-suggested docs
              selectedDocuments.forEach((doc) => {
                if (sources.get(doc.id) === 'ai-system') {
                  onDeselect(doc.id);
                }
              });
            }}
            className="px-2 py-0.5 rounded text-[10px] font-medium border border-amber-300 dark:border-amber-600 hover:bg-amber-100 dark:hover:bg-amber-800 dark:text-amber-200 transition-colors"
          >
            Limpiar
          </button>
        </div>
      )}

      {/* Selected documents list */}
      <div className="py-2">
        {selectedDocuments.map((doc) => {
          const source = sources.get(doc.id) || 'manual';
          const isAi = source === 'ai-system';
          const aiInfo = isAi ? reasonMap.get(doc.id) : undefined;
          const hoverText = aiInfo
            ? [aiInfo.reason, aiInfo.strategy && `Estrategia: ${aiInfo.strategy}`]
                .filter(Boolean)
                .join(' · ')
            : undefined;

          return (
            <div
              key={doc.id}
              onClick={() => onDeselect(doc.id)}
              onMouseEnter={(e) => onDocMouseEnter(doc, e)}
              onMouseLeave={onDocMouseLeave}
              className={`group/doc relative px-3 py-1.5 cursor-pointer flex items-center gap-2 transition-colors hover:${
                theme.colors.bg.secondary
              } ${
                isAi
                  ? `${theme.colors.bg.secondary} border-l-2 border-nb-accent`
                  : `border-l-2 border-transparent`
              }`}
            >
              {/* Checkbox — always checked */}
              <div className="flex-shrink-0">
                <div
                  className={`w-4 h-4 rounded flex items-center justify-center ${
                    theme.colors.bg.primary
                  } border-2 ${isAi ? 'border-nb-accent' : theme.colors.border.primary}`}
                >
                  <Check
                    className={`w-2.5 h-2.5 ${
                      isAi ? 'text-nb-accent' : theme.colors.text.secondary
                    }`}
                  />
                </div>
              </div>

              {/* Doc info */}
              <div className="flex-1 min-w-0">
                <div
                  className={`text-xs font-medium ${theme.colors.text.primary} truncate group-hover/doc:whitespace-normal`}
                >
                  {doc.company_name || doc.name || doc.original_filename || 'Documento sin nombre'}
                </div>
                {(doc.document_type || doc.document_period) && (
                  <div className="flex items-center gap-1 mt-0.5">
                    {doc.document_type && (
                      <span
                        className={`text-[10px] font-medium ${
                          doc.document_type === 'FECU'
                            ? 'text-blue-400'
                            : doc.document_type === 'ANALISIS'
                              ? 'text-emerald-400'
                              : doc.document_type === 'HE'
                                ? 'text-amber-400'
                                : theme.colors.text.secondary
                        }`}
                      >
                        {doc.document_type}
                      </span>
                    )}
                    {doc.document_type && doc.document_period && (
                      <span className={`text-[10px] ${theme.colors.text.muted}`}>•</span>
                    )}
                    {doc.document_period && (
                      <span className={`text-[10px] ${theme.colors.text.secondary}`}>
                        {formatPeriod(doc.document_period, doc.document_type)}
                      </span>
                    )}
                  </div>
                )}
                {/* AI reason — visible on hover only */}
                {aiInfo?.reason && (
                  <div
                    className={`text-[10px] ${theme.colors.text.secondary} mt-0.5 line-clamp-2 hidden group-hover/doc:block`}
                  >
                    {aiInfo.reason}
                  </div>
                )}
              </div>

              {/* Smart pill — AI-selected indicator (NN/Group: icon+color > color alone) */}
              {isAi && (
                <span className="flex-shrink-0 px-1.5 py-0.5 rounded-full bg-nb-accent/15 text-nb-accent text-[9px] font-semibold uppercase leading-none">
                  Smart
                </span>
              )}

              {/* Scope badge (right side) — consistent with Recientes */}
              <div className={`flex-shrink-0 ${theme.colors.text.muted}`}>
                {doc.scope === 'shared' ? (
                  <Globe className="w-3.5 h-3.5" />
                ) : (
                  <User className="w-3.5 h-3.5" />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tooltip */}
      {tooltip && <DocTooltip doc={tooltip.doc} anchor={tooltip.rect} />}

      {/* Loading indicator (no count — SectionHeader already shows it) */}
      {isSuggestionsLoading && (
        <div className={`px-3 py-1 flex items-center gap-1`}>
          <Loader2 className={`w-3 h-3 animate-spin ${theme.colors.text.secondary}`} />
          <span className={`text-[10px] ${theme.colors.text.secondary}`}>actualizando...</span>
        </div>
      )}
    </div>
  );
};

export default SelectedDocsTab;
